import type { useKagami } from "../state/useKagami";
import { Chip, SectionLabel, mutedColor } from "./primitives";

type K = ReturnType<typeof useKagami>;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const chipRow = { display: "flex", flexWrap: "wrap", gap: 5 } as const;

export function FilterRail({ k }: { k: K }) {
  const { filters, toggleIn } = k;

  return (
    <aside className="k-rail" data-open={k.filtersOpen ? "true" : "false"}>
      <div style={{ position: "sticky", top: 118, display: "flex", flexDirection: "column", gap: 18 }}>
        <div className="k-mob" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <SectionLabel style={{ margin: 0 }}>Filters</SectionLabel>
          <Chip onClick={() => k.setFiltersOpen(false)} style={{ marginLeft: "auto", padding: "7px 11px" }}>Done</Chip>
        </div>

        {/* The seed. Everything on the right is scored against this one work,
            so it's shown first and never hidden. */}
        <div>
          <SectionLabel>Tuned to</SectionLabel>
          <div style={{
            display: "flex", alignItems: "center", gap: 9, padding: "9px 10px", borderRadius: 8,
            background: "var(--color-surface)", boxShadow: "var(--shadow-sm)",
          }}>
            <div style={{
              width: 26, height: 36, borderRadius: 3, flex: "none",
              background: "linear-gradient(160deg, var(--color-accent-700), var(--color-neutral-900))",
            }} />
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontFamily: "var(--font-heading)", fontWeight: 500,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>{k.seed?.title ?? "—"}</div>
              <div style={{ fontSize: 11, color: mutedColor(50) }}>
                {k.seed ? `${k.seed.by} · ${k.seed.year}` : ""}
              </div>
            </div>
          </div>
        </div>

        <div>
          <SectionLabel>Genre</SectionLabel>
          <div style={chipRow}>
            {k.genreOptions.map((g) => (
              <Chip key={g} on={filters.genres.includes(g)} onClick={() => toggleIn("genres", g)}>{g}</Chip>
            ))}
          </div>
        </div>

        {k.moodOptions.length > 0 && (
          <div>
            <SectionLabel>Mood</SectionLabel>
            <div style={chipRow}>
              {k.moodOptions.map((m) => (
                <Chip key={m} on={filters.moods.includes(m)} onClick={() => toggleIn("moods", m)}>{cap(m)}</Chip>
              ))}
            </div>
          </div>
        )}

        <div>
          <SectionLabel>Minimum score</SectionLabel>
          <div style={chipRow}>
            {[0, 8, 8.5, 9].map((v) => (
              <Chip key={v} on={filters.minScore === v}
                onClick={() => k.setFilters((f) => ({ ...f, minScore: v }))}>
                {v ? `${v.toFixed(1)}+` : "Any"}
              </Chip>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel>Length</SectionLabel>
          <div style={chipRow}>
            {["short", "medium", "long"].map((l) => (
              <Chip key={l} on={filters.lens.includes(l)} onClick={() => toggleIn("lens", l)}>{cap(l)}</Chip>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel>Status</SectionLabel>
          <div style={chipRow}>
            {["finished", "airing", "hiatus"].map((s) => (
              <Chip key={s} on={filters.statuses.includes(s)} onClick={() => toggleIn("statuses", s)}>{cap(s)}</Chip>
            ))}
          </div>
        </div>

        <button className="btn btn-secondary" onClick={k.clearFilters} style={{ alignSelf: "flex-start" }}>
          Clear filters
        </button>
      </div>
    </aside>
  );
}
